import { liveQuery, type Subscription } from 'dexie';
import { APP_VERSION, API_VERSION, SYNC_SCHEMA_VERSION } from '@/config/appVersion';
import { deviceIdentity } from '@/api/deviceIdentity';
import { downloadBlob } from '@shared/utils/downloadAuthenticatedFile';
import { db } from './db';
import { setForcedUpdateBackupHandler } from './forcedUpdateBackup';
import type { SyncOperationType, SyncQueueItem } from './types';

/**
 * Sync-queue backup exporter (phase C.7). Fills the C.6 seam
 * (`setForcedUpdateBackupHandler`): before a *forced* update reloads onto a new
 * build, any queued operation that is legally critical must be offered as a
 * downloadable JSON backup, because a sync-schema bump on the new build could
 * otherwise strand it. The backup is the legal safety net.
 *
 * Two hard constraints shape this module:
 *
 *  1. The download MUST fire synchronously inside the user's click gesture —
 *     browsers block downloads started after an `await`/`setTimeout`/`.then`.
 *     Dexie reads are async, so we cannot read the queue at click time. Instead a
 *     Dexie `liveQuery` keeps an in-memory snapshot continuously fresh
 *     (`startSyncQueueSnapshot`), and the registered handler reads that snapshot
 *     synchronously (`runForcedUpdateBackup`).
 *  2. The auto-download fires only when at least one *legally critical* operation
 *     is pending. A queue of purely operational ops triggers nothing (an opt-in
 *     "download backup" button is separate UI, out of this seam's scope).
 *
 * The JSON itself always contains the *full, unfiltered* queue — the criticality
 * test only gates *whether* to download, never *what* goes in the file.
 */

/**
 * Operations whose loss has legal/compliance weight (fuel-reception lifecycle +
 * its evidence/signature attachments, and the incident logbook create + its
 * deferred signature/evidence). `inspection.create` and `generic.patch` are
 * operational and do not, on their own, trigger the forced-update backup.
 */
export const CRITICAL_SYNC_OPERATIONS: ReadonlySet<SyncOperationType> = new Set([
  'fuelReception.create',
  'fuelReception.patch',
  'fuelReception.start',
  'fuelReception.submit',
  'fuelReception.review',
  'fuelReception.reject',
  'fuelReception.complete',
  'fuelReception.reopen',
  'fuelReceptionEvidence.upload',
  'evidence.upload',
  'signature.attach',
  'incident.create',
  'incidentSignature.attach',
  'incidentEvidence.upload'
]);

/** Format version of the backup file itself — distinct from the sync-schema version. */
export const BACKUP_EXPORT_VERSION = 1 as const;

export type SyncQueueBackup = {
  exportVersion: typeof BACKUP_EXPORT_VERSION;
  exportedAt: string;
  appVersion: string;
  apiVersion: string;
  syncSchemaVersion: number;
  deviceId: string;
  items: SyncQueueItem[];
};

/** True when the queue holds at least one legally-critical operation. */
export const hasCriticalOperation = (items: SyncQueueItem[]): boolean =>
  items.some((item) => CRITICAL_SYNC_OPERATIONS.has(item.operation));

/** Builds the backup payload. `items` is included whole — never filtered. */
export const buildSyncQueueBackup = (
  items: SyncQueueItem[],
  now: Date = new Date()
): SyncQueueBackup => ({
  exportVersion: BACKUP_EXPORT_VERSION,
  exportedAt: now.toISOString(),
  appVersion: APP_VERSION,
  apiVersion: API_VERSION,
  syncSchemaVersion: SYNC_SCHEMA_VERSION,
  deviceId: deviceIdentity.getDeviceId(),
  items
});

/**
 * `fuelops-backup-<deviceId>-<YYYY-MM-DDTHH-mm-ss>.json`, UTC, with the time's
 * colons swapped for dashes so the name is valid on Windows filesystems.
 */
export const buildBackupFilename = (deviceId: string, now: Date = new Date()): string => {
  const stamp = now.toISOString().slice(0, 19).replaceAll(':', '-');
  return `fuelops-backup-${deviceId}-${stamp}.json`;
};

/** Synchronously builds the JSON and triggers the browser download. */
export const downloadSyncQueueBackup = (items: SyncQueueItem[], now: Date = new Date()): void => {
  const backup = buildSyncQueueBackup(items, now);
  const json = JSON.stringify(backup, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  downloadBlob(blob, buildBackupFilename(backup.deviceId, now));
};

/**
 * The actual forced-update handler logic, split out so it is unit-testable
 * without the liveQuery subscription: download only if something critical is
 * pending. Synchronous by construction.
 */
export const runForcedUpdateBackup = (items: SyncQueueItem[]): void => {
  if (hasCriticalOperation(items)) {
    downloadSyncQueueBackup(items);
  }
};

// --- liveQuery snapshot wiring (keeps the click-time read synchronous) ---

let queueSnapshot: SyncQueueItem[] = [];
let subscription: Subscription | null = null;

/** Returns the latest queue snapshot maintained by the liveQuery subscription. */
export const getSyncQueueSnapshot = (): SyncQueueItem[] => queueSnapshot;

/**
 * Starts (or restarts) the Dexie liveQuery that mirrors `syncQueue` into the
 * in-memory snapshot. Idempotent — re-calling tears down the previous
 * subscription first. Returns the subscription so callers can dispose it.
 */
export const startSyncQueueSnapshot = (): Subscription => {
  subscription?.unsubscribe();
  subscription = liveQuery(() => db.syncQueue.toArray()).subscribe({
    next: (items) => {
      queueSnapshot = items;
    }
  });
  return subscription;
};

/**
 * App-startup wiring: keep the snapshot fresh and register the synchronous
 * forced-update backup handler against the C.6 seam. Called once from `main.tsx`.
 */
export const registerForcedUpdateBackupHandler = (): void => {
  startSyncQueueSnapshot();
  setForcedUpdateBackupHandler(() => runForcedUpdateBackup(getSyncQueueSnapshot()));
};
